import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRsi } from '../../hooks/useRsi';
import { Table } from '../Table';
import type { Data, Field, Fields, Meta } from '../../types';
import { MULTI_VALUE_SEPARATOR } from '../../types';
import type { CalculatedColumn, DataGridHandle, FillEvent, RowHeightArgs, RowsChangeData } from 'react-data-grid';
import { addErrorsAndRunHooks, buildBlankRows } from '../dataMutations';
import { generateColumns } from '../ValidationStepColumns';
import Button from 'common/components/button/Button';
import toggleSwitchStyle from 'common/components/toggleSwitch/ToggleSwitch.module.scss'
import { useTranslation } from 'react-i18next';
import { FaCheckDouble, FaPlus, FaTrashAlt } from 'react-icons/fa';
import { useToasts } from 'react-toast-notifications';
import { Columns } from './MatchColumns';
import FileSelector from 'common/components/fileSelector/FileSelector';
import Label from 'common/components/label/Label';
import CustomFile from 'common/models/CustomFile';
import { Col, Row } from 'react-flexbox-grid';
import ReactTooltip from 'react-tooltip';
import { Item, Menu, useContextMenu, theme } from 'react-contexify';
import 'react-contexify/dist/ReactContexify.min.css';

type Props<T extends string> = {
  initialData: Data<T>[];
  matchedOptions: any[];
  matchedColumns: Columns<T>;
  file?: File;
  onPrevious: () => Promise<void>;
};

// Task #66347: quantas linhas em branco o botão "+" acrescenta de cada vez.
const ADD_ROWS_STEP = 10;

// Task #66347: alturas da linha — a expandida tem de dar para as etiquetas de uma célula multi-valor.
const ROW_HEIGHT = 35;
const EXPANDED_ROW_HEIGHT = 92;

// Task #66347: ações de linha disponíveis no menu de contexto da grelha.
const ROW_ACTIONS_MENU_ID = 'spreadsheet-row-actions';
type RowAction = 'insertAbove' | 'insertBelow' | 'duplicate' | 'copy' | 'cut' | 'paste' | 'delete';

// Task #66347: botão direito no cabeçalho — ações sobre a grelha toda (levar tudo para o Excel).
const COPY_ALL_MENU_ID = 'spreadsheet-copy-all';

// Task #66347: escreve no clipboard do sistema. O caminho moderno é assíncrono e exige contexto
// seguro; o textarea + execCommand fica como rede para os casos em que não está disponível.
const writeClipboardText = async (text: string) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    // sem permissão ou fora de contexto seguro — tenta o caminho antigo
  }

  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    return copied;
  } catch (error) {
    return false;
  }
};

// Task #66347: lê o clipboard do sistema. Só o Ctrl+V traz o conteúdo no próprio evento; a partir de
// um clique num menu é preciso pedi-lo, o que pode ser recusado (permissão, contexto não seguro) e
// devolve então vazio, para quem chama tratar disso.
const readClipboardText = async () => {
  try {
    return (await navigator.clipboard?.readText()) ?? '';
  } catch (error) {
    return '';
  }
};

// Task #66347: normalização usada para casar texto colado do Excel com as opções de um dropdown
// (sem acentos, sem espaços a mais, sem maiúsculas) — ex: "Todos os Riscos" → ALL_RISKS.
const normalizeForMatch = (value: string) =>
  value.normalize('NFD').replace(new RegExp('[\u0300-\u036f]', 'g'), '').trim().toUpperCase().replace(/\s+/g, ' ');

// Task #66347: uma linha s\u00f3 conta quando tem alguma coisa. As linhas em branco com que a grelha
// arranca n\u00e3o s\u00e3o erro nenhum \u2014 s\u00e3o espa\u00e7o por preencher.
// Task #66347: comparações de conteúdo — servem para não substituir o estado dos avisos por outro
// igual, que só forçava trabalho de render sem nada de novo para mostrar.
const sameKeys = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && Array.from(a).every(key => b.has(key));

const sameWarnings = (a: Map<string, string[]>, b: Map<string, string[]>) =>
  a.size === b.size && Array.from(a).every(([key, messages]) => {
    const other = b.get(key);
    return !!other && other.length === messages.length && other.every((message, index) => message === messages[index]);
  });

const isRowBlank = <T extends string>(row: Data<T>, fields: Fields<T>) =>
  fields.every(field => {
    const value = row[field.key as T];
    return value === undefined || value === null || value === '' || value === false;
  });

export const ValidationStep = <T extends string>({ initialData, file, onPrevious, matchedOptions, matchedColumns }: Props<T>) => {
  const { fields, onClose, onSubmit, rowHook, tableHook, allowInvalidSubmit, isCellEditable, getCellLockedReason, asyncRowValidation, showRowNumber,
    initialFilterByErrors, showAttachments, enablePaste, enableFillHandle, allowAddRows, ignoreBlankRows, renderGridFooter, renderToolbarActions,
    submitButtonText, validationMessage, startWithBlankRows, onBack, asPage, enableRowActions,
    onUploadFile, getFileName } = useRsi<T>();
  const { t } = useTranslation();

  // Task #66347: com ignoreBlankRows, uma linha ainda por preencher não fica pintada de vermelho —
  // caso contrário a grelha em branco abria com todas as células obrigatórias em erro.
  const validate = useCallback((rows: (Data<T> & Partial<Meta>)[]) => {
    const validated = addErrorsAndRunHooks<T>(rows, fields, rowHook, tableHook);
    if (!ignoreBlankRows) return validated;
    return validated.map(row => (isRowBlank(row, fields) ? { ...row, __errors: null } : row));
  }, [fields, rowHook, tableHook, ignoreBlankRows]);

  const [data, setData] = useState<(Data<T> & Meta)[]>(
    useMemo(() => validate(initialData), []),
  );
  const [selectedRows, setSelectedRows] = useState<ReadonlySet<number | string>>(new Set());
  const [filterByErrors, setFilterByErrors] = useState(initialFilterByErrors);
  const [showSubmitAlert, setShowSubmitAlert] = useState(false);
  const { addToast } = useToasts();
  const warningToast = useCallback((description: string) => { addToast(description, { appearance: 'warning' }) }, []);

  const [chargeAttachments, setChargeAttachments] = useState<CustomFile[]>([]);

  // Managed container for ReactTooltip portal — created once, cleaned up on unmount
  const tooltipContainerRef = useRef<HTMLDivElement | null>(null);
  if (!tooltipContainerRef.current) {
    tooltipContainerRef.current = document.createElement('div');
    tooltipContainerRef.current.setAttribute('data-tooltip-container', 'validation');
    document.body.appendChild(tooltipContainerRef.current);
  }
  useEffect(() => {
    return () => {
      if (tooltipContainerRef.current?.parentNode) {
        tooltipContainerRef.current.parentNode.removeChild(tooltipContainerRef.current);
        tooltipContainerRef.current = null;
      }
    };
  }, []);

  const updateData = useCallback(
    (rows: typeof data) => {
      setData(validate(rows));
    },
    [setData, validate],
  );

  const deleteSelectedRows = () => {
    if (selectedRows.size) {
      const newData = data.filter(value => !selectedRows.has(value.__index));
      updateData(newData);
      setSelectedRows(new Set());
    }
  };

  // Task #66347: ações que substituem linhas (replicar) deixam o __index anterior para trás — a linha
  // modelo desaparece e as novas nascem com __index próprio. Sem limpar os ids que já não existem, o
  // contador dizia mais linhas do que as marcadas e o replicar recusava-se a correr por achar que
  // havia várias selecionadas.
  useEffect(() => {
    setSelectedRows(current => {
      if (!current.size) return current;
      const alive = new Set<string>(data.map(row => row.__index));
      const kept = Array.from(current).filter(id => alive.has(String(id)));
      return kept.length === current.size ? current : new Set(kept);
    });
  }, [data]);

  const updateRow = useCallback(
    (rows: typeof data, changedData?: RowsChangeData<(typeof data)[number]>) => {
      const changes = changedData?.indexes.reduce(
        (acc, index) => {
          // when data is filtered val !== actual index in data
          const realIndex = data.findIndex(value => value.__index === rows[index].__index);
          acc[realIndex] = rows[index];
          return acc;
        },
        {} as Record<number, (typeof data)[number]>,
      );
      const newData = Object.assign([], data, changes);
      updateData(newData);

    },
    [data, updateData],
  );

  // Task #66078: best-effort inline warnings from server-side validation.
  // Task #66220: guardados pelo __index estável da linha e não pela ordem — com a ordem, apagar uma
  // linha fazia os avisos das seguintes passarem a apontar para a linha errada.
  const [warningsByRow, setWarningsByRow] = useState<Map<string, string[]>>(new Map());
  // Task #66347: linhas cujos avisos são todos informativos — pintam-se com outro tom, porque
  // vermelho num "este contrato já existe e vai ser atualizado" lê-se como erro.
  const [infoRows, setInfoRows] = useState<Set<string>>(new Set());
  // Task #66347: linhas expandidas (só faz sentido havendo colunas multi-valor, ver hasMultiValueColumn).
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const asyncRunRef = useRef(0);
  const asyncRowValidationRef = useRef(asyncRowValidation);
  asyncRowValidationRef.current = asyncRowValidation;

  // Task #66347: que preenchimento automático já foi aplicado a cada linha (__index → valuesKey).
  const appliedFillsRef = useRef<Map<string, string>>(new Map());
  // O efeito da validação só depende dos dados; a escrita de volta passa por uma ref para não o
  // recriar a cada render (recriá-lo cancelaria a validação a meio).
  const updateDataRef = useRef(updateData);
  updateDataRef.current = updateData;

  // Stable 1-based line for each row (by its unique __index), independent of the error filter.
  const lineByIndex = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((row, i) => map.set(row.__index, i + 1));
    return map;
  }, [data]);

  // Refs let the "#" column formatter read the latest line/warnings without rebuilding columns.
  const lineByIndexRef = useRef(lineByIndex);
  lineByIndexRef.current = lineByIndex;
  const warningsByRowRef = useRef(warningsByRow);
  warningsByRowRef.current = warningsByRow;
  const infoRowsRef = useRef(infoRows);
  infoRowsRef.current = infoRows;
  const expandedRowsRef = useRef(expandedRows);
  expandedRowsRef.current = expandedRows;

  const getRowLine = useCallback((row: Data<T> & Meta) => lineByIndexRef.current.get(row.__index), []);
  const getRowWarnings = useCallback((row: Data<T> & Meta) => warningsByRowRef.current.get(row.__index), []);
  const getRowIsInfo = useCallback((row: Data<T> & Meta) => infoRowsRef.current.has(row.__index), []);

  // A expansão só se justifica quando há uma célula que não cabe fechada.
  const hasMultiValueColumn = useMemo(() => fields.some(field => field.fieldType.type === 'multiselect'), [fields]);

  // Expandir só faz sentido quando há mesmo coisas escondidas: fechada, a célula mostra a primeira
  // etiqueta e um "+N". Com zero ou uma, a seta não revelava nada e só enchia a coluna de ruído.
  const canExpandRow = useCallback((row: Data<T> & Meta) => fields.some(field => {
    if (field.fieldType.type !== 'multiselect') return false;
    const value = (row[field.key as T] as string) || '';
    return value.split(MULTI_VALUE_SEPARATOR).filter(Boolean).length > 1;
  }), [fields]);
  const isRowExpanded = useCallback((row: Data<T> & Meta) => expandedRowsRef.current.has(row.__index), []);
  const toggleRowExpanded = useCallback((row: Data<T> & Meta) => {
    setExpandedRows(current => {
      const next = new Set(current);
      if (!next.delete(row.__index)) next.add(row.__index);
      return next;
    });
  }, []);
  // O RowHeightArgs do react-data-grid também cobre linhas de agrupamento (que aqui não existem).
  const rowHeight = useCallback((args: RowHeightArgs<Data<T> & Meta>) =>
    ('__index' in args.row && expandedRows.has((args.row as Meta).__index) ? EXPANDED_ROW_HEIGHT : ROW_HEIGHT), [expandedRows]);

  // Run the opt-in async validation on mount and (debounced) after edits; ignore stale responses.
  // Task #66220: a revalidação corre a cada alteração dos dados (editar uma célula ou apagar linhas) e
  // os avisos são limpos de imediato — qualquer alteração pode invalidar avisos de outras linhas (os
  // duplicados são cruzados), e mostrar o resultado da corrida anterior enquanto se revalida dava a
  // ideia de que nada tinha mudado. O runId é incrementado já aqui, para uma resposta que venha a
  // caminho de dados antigos não voltar a pintar avisos desatualizados.
  useEffect(() => {
    if (!asyncRowValidationRef.current) return;
    const rows = data;
    const runId = ++asyncRunRef.current;
    setWarningsByRow(current => (current.size > 0 ? new Map() : current));
    setInfoRows(current => (current.size > 0 ? new Set<string>() : current));

    const timer = setTimeout(() => {
      asyncRowValidationRef.current!(rows as Data<T>[])
        .then(results => {
          if (runId !== asyncRunRef.current) return; // stale response, the data changed meanwhile
          const map = new Map<string, string[]>();
          const infoOnly = new Set<string>();
          // Task #66347: valores conhecidos a preencher (ver `values` em asyncRowValidation).
          const filled: (Data<T> & Meta)[] = [...rows];
          let hasFills = false;

          (results ?? []).forEach(r => {
            // O `line` é 1-based sobre as linhas enviadas nesta corrida.
            const row = rows[r.line - 1];
            if (!row) return;

            if (r?.warnings?.length) {
              map.set(row.__index, r.warnings.map(w => w.message));
              if (r.warnings.every(w => w.level === 'info')) {
                infoOnly.add(row.__index);
              }
            }

            // Uma vez por linha e por registo correspondente: se o utilizador apagar depois um campo,
            // não voltamos a escrevê-lo por cima.
            if (r.values && r.valuesKey && appliedFillsRef.current.get(row.__index) !== r.valuesKey) {
              appliedFillsRef.current.set(row.__index, r.valuesKey);
              const patch: Record<string, unknown> = {};
              Object.entries(r.values).forEach(([key, value]) => {
                const current = row[key as T];
                // Só preenche o que está vazio — nunca substitui o que já foi escrito.
                if ((current === undefined || current === null || current === '') && value !== undefined && value !== '') {
                  patch[key] = value;
                }
              });
              if (Object.keys(patch).length) {
                filled[r.line - 1] = { ...row, ...patch } as Data<T> & Meta;
                hasFills = true;
              }
            }
          });

          // Task #66347: só troca o estado quando os avisos mudam mesmo. Um Map novo com o mesmo
          // conteúdo reconstruía as colunas e o react-data-grid remontava as células — o que fechava
          // o menu de uma célula multi-valor a cada resposta da validação.
          setWarningsByRow(current => (sameWarnings(current, map) ? current : map));
          setInfoRows(current => (sameKeys(current, infoOnly) ? current : infoOnly));
          if (hasFills) updateDataRef.current(filled);
        })
        .catch(() => { /* inline warnings are best-effort — never throw */ });
    }, 400);
    return () => clearTimeout(timer);
    // Só os dados disparam a revalidação: o callback vive numa ref para um render do ecrã que a
    // recrie (é uma arrow inline) não cancelar uma validação a meio nem limpar os avisos sem razão.
  }, [data]);

  const rowClass = useCallback((row: Data<T> & Meta) => {
    if (!asyncRowValidation || !warningsByRow.has(row.__index)) return undefined;
    return infoRows.has(row.__index) ? 'rdg-row-info' : 'rdg-row-warning';
  }, [asyncRowValidation, warningsByRow, infoRows]);

  const unmappedKeys = useMemo(() => {
    // Task #66347: "não mapeou dados" é conversa do passo de mapear colunas de um ficheiro. Numa
    // grelha que arranca em branco não há mapeamento nenhum, e o aviso aparecia em todas as colunas.
    if (startWithBlankRows) return new Set<T>();

    const mappedKeys = new Set(
      matchedColumns
        .filter(col => 'value' in col)
        .map(col => ('value' in col ? col.value : '') as T),
    );
    return new Set(fields.filter(f => !mappedKeys.has(f.key as T)).map(f => f.key as T));
  }, [fields, matchedColumns, startWithBlankRows]);

  const dateFormats = useMemo(() => {
    const formats = new Map<string, string>();
    matchedOptions.forEach((opt: any) => {
      if (opt.column === 'date' || opt.column === 'paymentDate') {
        formats.set(opt.column, opt.value);
      }
    });
    return formats;
  }, [matchedOptions]);

  const columns = useMemo(
    () => generateColumns(
      fields,
      unmappedKeys,
      t('spreadsheet_import.validation_step.unmapped_column_tooltip') as string,
      dateFormats,
      format => t('spreadsheet_import.validation_step.date_format_tooltip', { format }) as string,
      isCellEditable,
      getCellLockedReason,
      showRowNumber,
      getRowLine,
      getRowWarnings,
      getRowIsInfo,
      isRowExpanded,
      hasMultiValueColumn ? toggleRowExpanded : undefined,
      canExpandRow,
      onUploadFile,
      getFileName,
      t('spreadsheet_import.validation_step.attach_file') as string,
      t('spreadsheet_import.validation_step.remove_file') as string,
    ),
    // Task #66078: warningsByRow na dependência força o rebuild das colunas quando a revalidação
    // muda os avisos → a célula "#" re-renderiza e o triângulo aparece/desaparece sem ser preciso clicar.
    [fields, unmappedKeys, dateFormats, isCellEditable, getCellLockedReason, showRowNumber, getRowLine, getRowWarnings, getRowIsInfo, isRowExpanded, toggleRowExpanded, canExpandRow, hasMultiValueColumn, warningsByRow, infoRows, expandedRows, onUploadFile, getFileName, t],
  );

  useEffect(() => {
    const timer = setTimeout(() => ReactTooltip.rebuild(), 50);
    return () => clearTimeout(timer);
  });

  const tableData = useMemo(() => {
    let result = [];
    if (filterByErrors) {
      result = data.filter(value => {
        if (value?.__errors) {
          return Object.values(value.__errors)?.filter(err => err.level === 'error').length;
        }
        return false;
      });
    } else {
      result = data;
    }

    return result;

  }, [data, filterByErrors]);


  // O menu de contexto resolve a linha pelo índice que o DOM da grelha expõe — que é sobre as linhas
  // visíveis, não sobre `data` (podem estar filtradas).
  const tableDataRef = useRef(tableData);
  tableDataRef.current = tableData;

  // Task #66347: arrastar o canto da célula replica o valor pelas linhas por onde passa (fill handle
  // do Excel). O grid desenha o quadradinho sozinho a partir do momento em que recebe o onFill.
  const onFill = useCallback(({ columnKey, sourceRow, targetRow }: FillEvent<Data<T> & Meta>) => {
    const key = columnKey as T;
    // Respeita as células bloqueadas por regra de negócio (ex: periodicidade num contrato de compra).
    if (isCellEditable && !isCellEditable(targetRow, key)) return targetRow;
    return { ...targetRow, [key]: sourceRow[key] };
  }, [isCellEditable]);

  // Task #66347: primeiro clique seleciona a célula, segundo é que abre a edição — como no Excel.
  // O grid não tem esse modo (ou abre logo ao clique, ou só ao duplo/Enter), por isso guarda-se a
  // última célula clicada e, repetindo-se, pede-se-lhe para abrir o editor.
  const gridRef = useRef<DataGridHandle>(null);
  const lastClickedCellRef = useRef<{ rowIdx: number; idx: number } | null>(null);

  const onCellClick = useCallback((row: Data<T> & Meta, column: CalculatedColumn<Data<T> & Meta>) => {
    const rowIdx = tableDataRef.current.findIndex(value => value.__index === row.__index);
    if (rowIdx < 0) return;

    const previous = lastClickedCellRef.current;
    lastClickedCellRef.current = { rowIdx, idx: column.idx };

    if (previous && previous.rowIdx === rowIdx && previous.idx === column.idx) {
      gridRef.current?.selectCell({ rowIdx, idx: column.idx }, true);
    }
  }, []);

  const rowKeyGetter = useCallback((row: Data<T> & Meta) => row.__index, []);

  // Task #66347: acrescentar linhas em branco ao fundo da grelha (grelha que arranca sem ficheiro).
  const addBlankRows = useCallback(() => {
    updateData([...data, ...buildBlankRows(fields, ADD_ROWS_STEP)] as (Data<T> & Meta)[]);
  }, [data, fields, updateData]);

  // Task #66347: colar blocos de células vindos do Excel. O `onPaste` do react-data-grid desta versão
  // só dispara depois de uma cópia feita dentro da própria grelha, por isso tratamos o evento nativo.
  // A âncora é a célula selecionada — o grid marca-a com aria-selected e expõe aria-colindex/rowindex.
  const gridWrapperRef = useRef<HTMLDivElement | null>(null);

  const resolvePastedValue = useCallback((field: Field<T>, raw: string): string | boolean => {
    const text = (raw ?? '').replace(/^"|"$/g, '').trim();
    if (!text) return '';

    const matchOption = (options: readonly { label: string; value: string }[], value: string) => {
      const normalized = normalizeForMatch(value);
      const hit = options.find(option => normalizeForMatch(option.value) === normalized)
        ?? options.find(option => normalizeForMatch(option.label) === normalized);
      // Sem correspondência devolvemos o texto tal como veio: fica visível na célula e é o motor de
      // validação que o assinala — melhor do que adivinhar por aproximação e gravar o registo errado.
      return hit?.value ?? value.trim();
    };

    switch (field.fieldType.type) {
      case 'select':
        return matchOption(field.fieldType.options, text);
      case 'multiselect':
        return text.split(/[;,]/).map(part => part.trim()).filter(Boolean)
          .map(part => matchOption(field.fieldType.type === 'multiselect' ? field.fieldType.options : [], part))
          .join(MULTI_VALUE_SEPARATOR);
      case 'checkbox':
        return ['1', 'TRUE', 'SIM', 'YES', 'OUI', 'X'].includes(normalizeForMatch(text));
      default:
        return text;
    }
  }, []);

  const findPasteAnchor = useCallback(() => {
    const cell = gridWrapperRef.current?.querySelector('[role="gridcell"][aria-selected="true"]');
    if (!cell) return null;

    const columnIndex = Number(cell.getAttribute('aria-colindex')) - 1;
    const rowIndex = Number(cell.closest('[role="row"]')?.getAttribute('aria-rowindex')) - 2; // 1 = cabeçalho
    const columnKey = columns[columnIndex]?.key;
    const row = tableData[rowIndex];
    if (!row) return null;

    // As primeiras colunas da linha são o "#" e o visto de seleção, que não são dados. Estando o
    // cursor numa delas, cola-se a partir da primeira coluna de dados dessa linha em vez de desistir:
    // clicar no número da linha e colar é o gesto natural para preencher a linha toda.
    const fieldIndex = fields.findIndex(field => field.key === columnKey);

    return { fieldIndex: fieldIndex < 0 ? 0 : fieldIndex, row };
  }, [columns, fields, tableData]);

  // Task #66347: o caminho inverso do colar — o que sai tem de ser o que o `resolvePastedValue` sabe
  // voltar a ler, por isso vão etiquetas (e não ids), multi-valor separado por `;` e Sim/Não nos
  // checkboxes. Tabs e quebras de linha dentro de um valor partiriam a grelha do Excel.
  const cellToText = useCallback((row: Data<T> & Meta, field: Field<T>) => {
    const raw = row[field.key as T];
    let text: unknown = raw;

    switch (field.fieldType.type) {
      case 'checkbox':
        text = raw ? t('common.yes') : t('common.no');
        break;
      case 'select':
        text = field.fieldType.options.find(option => option.value === raw)?.label ?? raw ?? '';
        break;
      case 'multiselect': {
        const options = field.fieldType.options;
        text = String(raw ?? '').split(MULTI_VALUE_SEPARATOR)
          .map(part => part.trim()).filter(Boolean)
          .map(value => options.find(option => option.value === value)?.label ?? value)
          .join('; ');
        break;
      }
    }

    return String(text ?? '').replace(/[\t\r\n]+/g, ' ');
  }, [t]);

  const rowsToTsv = useCallback((rows: (Data<T> & Meta)[], withHeader: boolean) => {
    const lines = withHeader ? [fields.map(field => field.label).join('\t')] : [];
    rows.forEach(row => lines.push(fields.map(field => cellToText(row, field as unknown as Field<T>)).join('\t')));
    return lines.join('\n');
  }, [fields, cellToText]);

  // Task #66347: o coração do colar, partilhado pelo Ctrl+V e pelo "colar linha" do menu de contexto
  // — as duas vias têm de fazer exatamente o mesmo, senão o utilizador nunca sabe o que esperar.
  const applyPastedText = useCallback((text: string, anchor: { fieldIndex: number; row: Data<T> & Meta }) => {
    const matrix = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '').split('\n').map(line => line.split('\t'));
    if (!matrix.length) return;

    const startRowIndex = data.findIndex(row => row.__index === anchor.row.__index);
    if (startRowIndex < 0) return;

    const newData = [...data] as (Data<T> & Partial<Meta>)[];
    matrix.forEach((cells, rowOffset) => {
      const targetIndex = startRowIndex + rowOffset;
      if (targetIndex >= newData.length) {
        newData.push(buildBlankRows(fields, 1)[0]);
      }
      const row = { ...newData[targetIndex] };
      cells.forEach((raw, columnOffset) => {
        const field = fields[anchor.fieldIndex + columnOffset];
        if (!field) return;
        const key = field.key as T;
        // Uma célula de ficheiro guarda uma referência, não texto: colar por cima só lá punha lixo.
        if (field.fieldType.type === 'file') return;
        // Respeita as células bloqueadas por regra de negócio (ex: periodicidade num contrato de compra).
        if (isCellEditable && !isCellEditable(row as Data<T> & Meta, key)) return;
        (row as Record<string, unknown>)[key] = resolvePastedValue(field as unknown as Field<T>, raw);
      });
      newData[targetIndex] = row;
    });

    updateData(newData as (Data<T> & Meta)[]);
  }, [data, fields, isCellEditable, resolvePastedValue, updateData]);

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!enablePaste) return;

    const text = event.clipboardData?.getData('text/plain');
    if (!text) {
      // O clipboard do sistema pode ficar vazio (permissões, janela sem foco). Havendo uma linha
      // copiada aqui dentro, o Ctrl+V faz o mesmo que o "colar linha" do menu de contexto.
      const anchorRow = findPasteAnchor();
      if (!anchorRow || !rowClipboardRef.current) return;
      event.preventDefault();
      const target = data.findIndex(row => row.__index === anchorRow.row.__index);
      if (target < 0) return;
      const replaced = [...data] as (Data<T> & Partial<Meta>)[];
      replaced[target] = { ...rowClipboardRef.current, __index: data[target].__index };
      updateData(replaced as (Data<T> & Meta)[]);
      return;
    }

    // Um bloco do Excel traz tabulações e/ou mudanças de linha; um valor solto não traz nada disso.
    const isBlock = /[\t\n\r]/.test(text);
    // Com o editOnClick, clicar numa célula abre logo o editor — sem esta distinção o `<input>`
    // ficava com o bloco todo enfiado numa célula só, que era o que estava a acontecer.
    const editorElement = (event.target as HTMLElement)?.closest?.('input, textarea, [contenteditable="true"]');
    if (editorElement && !isBlock) return;

    const matrix = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '').split('\n').map(line => line.split('\t'));
    if (!matrix.length) return;

    const anchor = findPasteAnchor();
    if (!anchor) {
      warningToast(t('spreadsheet_import.validation_step.paste_select_cell_first') as string);
      return;
    }
    event.preventDefault();

    // Fecha o editor descartando o que lá estava: a fechar por si (blur) gravaria o valor antigo
    // por cima da célula que acabámos de colar.
    if (editorElement) {
      editorElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    }

    applyPastedText(text, anchor);
  }, [enablePaste, findPasteAnchor, applyPastedText, data, updateData]);

  const isBlankRow = useCallback((row: Data<T> & Meta) => isRowBlank(row, fields), [fields]);

  // Task #66347: ações de linha do Excel (inserir acima/abaixo, duplicar, cortar/copiar/colar,
  // eliminar) num menu de contexto sobre a grelha. O botão direito é onde as pessoas as procuram.
  const rowClipboardRef = useRef<Data<T> | null>(null);
  const { show: showRowMenu } = useContextMenu({ id: ROW_ACTIONS_MENU_ID });
  const { show: showCopyAllMenu } = useContextMenu({ id: COPY_ALL_MENU_ID });

  const valuesOf = useCallback((row: Data<T> & Meta) => {
    const { __index, __errors, ...values } = row;
    return values as unknown as Data<T>;
  }, []);

  // Task #66347: Ctrl+C sobre a grelha leva as linhas para o Excel. Com linhas marcadas copia-as
  // todas; sem marcação, copia a linha da célula selecionada. O `setData` do próprio evento é
  // síncrono, por isso não depende de permissões de clipboard.
  const handleCopy = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!enablePaste) return;
    // Com um editor aberto quem manda é o comportamento nativo do input.
    if ((event.target as HTMLElement)?.closest?.('input, textarea, [contenteditable="true"]')) return;

    const anchor = selectedRows.size ? null : findPasteAnchor();
    const rows = selectedRows.size
      ? data.filter(row => selectedRows.has(row.__index))
      : (anchor ? [anchor.row] : []);
    if (!rows.length) return;

    event.preventDefault();
    event.clipboardData.setData('text/plain', rowsToTsv(rows, false));

    // Mantém o clipboard interno em sintonia: copiar com Ctrl+C e colar pelo menu de contexto
    // tem de dar a mesma linha.
    if (rows.length === 1) {
      rowClipboardRef.current = valuesOf(rows[0]);
    }
  }, [enablePaste, selectedRows, data, findPasteAnchor, rowsToTsv, valuesOf]);

  // Ctrl+X: o mesmo que o "cortar linha" do menu de contexto.
  const handleCut = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!enablePaste) return;
    if ((event.target as HTMLElement)?.closest?.('input, textarea, [contenteditable="true"]')) return;

    const anchor = selectedRows.size ? null : findPasteAnchor();
    const rows = selectedRows.size
      ? data.filter(row => selectedRows.has(row.__index))
      : (anchor ? [anchor.row] : []);
    if (!rows.length) return;

    event.preventDefault();
    event.clipboardData.setData('text/plain', rowsToTsv(rows, false));

    if (rows.length === 1) {
      rowClipboardRef.current = valuesOf(rows[0]);
    }

    const cutIds = new Set(rows.map(row => row.__index));
    updateData(data.map(row => (cutIds.has(row.__index)
      ? { ...buildBlankRows(fields, 1)[0], __index: row.__index }
      : row)) as (Data<T> & Meta)[]);
  }, [enablePaste, selectedRows, data, fields, findPasteAnchor, rowsToTsv, valuesOf, updateData]);

  const copyRows = useCallback(async (rows: (Data<T> & Meta)[], withHeader: boolean) => {
    const copied = await writeClipboardText(rowsToTsv(rows, withHeader));
    if (copied) {
      addToast(t('spreadsheet_import.validation_step.copy_done'), { appearance: 'success' });
      return;
    }
    warningToast(t('spreadsheet_import.validation_step.copy_error') as string);
  }, [rowsToTsv, addToast, warningToast, t]);

  // O clipboard do sistema pode recusar (sem permissão, janela sem foco). Falhando, o utilizador tem
  // de saber, senão o Ctrl+V a seguir parece avariado. A linha continua no clipboard interno, que o
  // "colar linha" e o próprio Ctrl+V usam como rede.
  const copyRowToClipboard = useCallback(async (row: Data<T> & Meta) => {
    const copied = await writeClipboardText(rowsToTsv([row], false));
    if (!copied) {
      warningToast(t('spreadsheet_import.validation_step.copy_error') as string);
    }
  }, [rowsToTsv, warningToast, t]);

  const indexInData = useCallback((rowIndex: number) => {
    const row = tableDataRef.current[rowIndex];
    return row ? data.findIndex(value => value.__index === row.__index) : -1;
  }, [data]);

  const onGridContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    // Task #66347: no cabeçalho o botão direito não é sobre uma linha — é sobre a grelha toda.
    if (enablePaste && (event.target as HTMLElement)?.closest?.('[role="columnheader"]')) {
      event.preventDefault();
      showCopyAllMenu(event);
      return;
    }

    if (!enableRowActions) return;
    const rowElement = (event.target as HTMLElement)?.closest?.('[role="row"]');
    // aria-rowindex é 1-based e conta com a linha de cabeçalho.
    const rowIndex = Number(rowElement?.getAttribute('aria-rowindex')) - 2;
    if (!(rowIndex >= 0) || !tableDataRef.current[rowIndex]) return;
    event.preventDefault();
    showRowMenu(event, { props: { rowIndex } });
  }, [enableRowActions, showRowMenu]);

  // O "colar linha" do menu tem de dar o mesmo que o Ctrl+V: primeiro o clipboard do sistema (é lá
  // que está o que veio do Excel), e só se ele não estiver acessível é que se usa a linha copiada
  // aqui dentro. Sem isto, o menu ignorava tudo o que não tivesse sido copiado na própria grelha.
  const pasteFromClipboard = useCallback(async (targetIndex: number) => {
    const row = data[targetIndex];
    if (!row) return;

    const text = await readClipboardText();
    if (text) {
      applyPastedText(text, { fieldIndex: 0, row });
      return;
    }

    if (rowClipboardRef.current) {
      const replaced = [...data] as (Data<T> & Partial<Meta>)[];
      replaced[targetIndex] = { ...rowClipboardRef.current, __index: row.__index };
      updateData(replaced as (Data<T> & Meta)[]);
      return;
    }

    warningToast(t('spreadsheet_import.validation_step.paste_nothing_to_paste') as string);
  }, [data, applyPastedText, updateData, warningToast, t]);

  const runRowAction = useCallback((action: RowAction, rowIndex: number) => {
    const target = indexInData(rowIndex);
    if (target < 0) return;

    const newData = [...data] as (Data<T> & Partial<Meta>)[];
    const blank = () => buildBlankRows(fields, 1)[0];

    switch (action) {
      case 'insertAbove':
        newData.splice(target, 0, blank());
        break;
      case 'insertBelow':
        newData.splice(target + 1, 0, blank());
        break;
      case 'duplicate':
        newData.splice(target + 1, 0, { ...valuesOf(data[target]) });
        break;
      // Copiar e cortar escrevem também no clipboard do sistema: sem isso, copiar pelo menu e colar
      // com Ctrl+V dava o conteúdo anterior do clipboard, e as duas formas de copiar divergiam.
      case 'copy':
        rowClipboardRef.current = valuesOf(data[target]);
        copyRowToClipboard(data[target]);
        return;
      case 'cut':
        rowClipboardRef.current = valuesOf(data[target]);
        copyRowToClipboard(data[target]);
        newData[target] = { ...blank(), __index: data[target].__index };
        break;
      case 'paste':
        // Assíncrono porque tem de ir pedir o clipboard do sistema; sai já para não cair no
        // updateData no fim, que é do caminho síncrono.
        pasteFromClipboard(target);
        return;
      case 'delete':
        newData.splice(target, 1);
        break;
    }

    updateData(newData as (Data<T> & Meta)[]);
  }, [data, fields, indexInData, updateData, valuesOf, copyRowToClipboard, pasteFromClipboard]);

  const submitData = async () => {
    const rowsToSubmit = ignoreBlankRows ? data.filter(row => !isBlankRow(row)) : data;

    const calculatedData = rowsToSubmit.reduce(
      (acc, value) => {
        const { __index, __errors, ...values } = value;
        if (__errors) {
          for (const key in __errors) {
            if (__errors[key].level === 'error') {
              acc.invalidData.push(values as unknown as Data<T>);
              return acc;
            }
          }
        }
        acc.validData.push(values as unknown as Data<T>);
        return acc;
      },
      { validData: [] as Data<T>[], invalidData: [] as Data<T>[], all: data, matchedOptions, matchedColumns, chargeAttachments },
    );

    if (!calculatedData.validData.length) {
      warningToast(t('import_charges.cannot_save_without_valid_lines'));
      return;
    }

    onSubmit(calculatedData, file, () => {
      setShowSubmitAlert(false);
      onClose();
    }, () => {
      //console.log('error')
    });

  };

  const onContinue = () => {

    if (hasAttachmentRequired && !chargeAttachments?.length) {
      warningToast(t('charges.attachment_required'));
      setShowSubmitAlert(true);
      return;
    }

    const errorCount = data.filter(value => {
      if (ignoreBlankRows && isBlankRow(value)) return false;
      if (value?.__errors) {
        return !!Object.values(value.__errors)?.filter(err => err.level === 'error').length;
      }
      return false;
    }).length;
    if (!errorCount) {
      submitData();
    } else {
      warningToast(t('spreadsheet_import.alerts.submit_incomplete.body_text_with_count' as any, { count: errorCount }) as string);
      setFilterByErrors(true);
      setShowSubmitAlert(true);
    }
  };

  //files
  // >>> validação do attachmentRequired
  let hasAttachmentRequired = false;
  const typeCodeField = fields.find(f => f.key === 'typeCode');
  if (typeCodeField?.fieldType.type === 'select') {
    const requiredAttachmentValues = new Set(
      typeCodeField.fieldType.options.filter((opt: any) => opt.attachmentRequired === true)?.map(opt => opt.value)
    );

    hasAttachmentRequired = data.some((row: any) =>
      requiredAttachmentValues.has(row.typeCode)
    );

  }

  const setValuesFiles = (files: CustomFile[]) => {
    const filesAux = files.map(file => {
      (file as any).containerName = 'CHARGE';
      // Garante id único, mesmo que nome seja igual
      if (!file.id) {
        (file as any).id = `${file.name}_${file.lastModified}_${Math.random()}`;
      }
      return file;
    });

    setChargeAttachments(filesAux);

  }

  const onRemoveFile = (file: CustomFile) => {
    if (!file.id) return;
    // Remove do array pelo id
    setChargeAttachments(prev => prev.filter(f => f.id !== file.id));
  }

  return (
    <>
      {tooltipContainerRef.current && createPortal(<ReactTooltip id="validation-unmapped-tooltip" place="top" type="dark" effect="solid" />, tooltipContainerRef.current)}
      {/* Task #66347: em página, a grelha estica até ao fundo em vez de ficar com a altura fixa que o
          react-data-grid usa por omissão — quem faz scroll é a própria grelha. */}
      <div className="spreadsheet-step-scroll" style={asPage
        ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
        : { flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
      <b>{validationMessage ?? t('spreadsheet_import.validation_step.valid_message')}</b>

      <div style={{ display: 'flex', marginTop: '2rem', marginBottom: '1rem', justifyContent: 'flex-start', alignItems: 'center', gap: '8px' }} >

        <Button preset={'primary'} size={'small'} onlyIcon={true}
          onClick={() => setSelectedRows(new Set(tableData.map(x => x.__index)))}
          style={{ marginLeft: 0 }}
          disabledMessage={t('spreadsheet_import.validation_step.select_all_lines') as string}
        >
          <FaCheckDouble />
        </Button>

        <Button preset={'danger'} size={'small'} onlyIcon={true}
          onClick={deleteSelectedRows}
          disabledMessage={t('spreadsheet_import.validation_step.discard_button_title') as string}
        >
          <FaTrashAlt />
        </Button>

        {allowAddRows && <Button preset={'primary'} size={'small'} onlyIcon={true}
          onClick={addBlankRows}
          disabledMessage={t('spreadsheet_import.validation_step.add_rows_button_title', { count: ADD_ROWS_STEP }) as string}
        >
          <FaPlus />
        </Button>}

        {renderToolbarActions?.({
          rows: data,
          selectedRowIds: Array.from(selectedRows).map(String),
          setRows: rows => updateData(rows as (Data<T> & Meta)[]),
        })}

        {selectedRows.size > 0 && <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginLeft: 10 }}
        >
          {selectedRows.size == 1 && <label>{t('import_charges.counter_selected_line')} </label>}
          {selectedRows.size > 1 && <label>{t('import_charges.counter_selected_lines', { counter: selectedRows.size })} </label>}
        </div>}

        <div style={{ display: 'flex', flex: 1, justifyContent: 'flex-end', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label
            className={`${toggleSwitchStyle.toggleSwitch} ${toggleSwitchStyle.enabled}`}>
            <input
              type='checkbox'
              checked={filterByErrors}
              onChange={() => {
                if (filterByErrors == false) {
                  setSelectedRows(new Set())
                }
                setFilterByErrors(!filterByErrors);
              }}
            />
            <span className={toggleSwitchStyle.switch}></span>
          </label>
          <label style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('spreadsheet_import.validation_step.filter_switch_title')} </label>
        </div>

      </div>

      <div style={asPage ? { flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { flexGrow: 1 }}
        ref={gridWrapperRef} onPaste={handlePaste} onCopy={handleCopy} onCut={handleCut} onContextMenu={onGridContextMenu}>
        <Table
          gridRef={gridRef}
          onRowClick={onCellClick}
          style={asPage ? { height: '100%' } : undefined}
          rowKeyGetter={rowKeyGetter}
          onFill={enableFillHandle ? onFill : undefined}
          rowHeight={hasMultiValueColumn ? rowHeight : undefined}
          rows={tableData}
          onRowsChange={updateRow}
          columns={columns}
          rowClass={rowClass}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}

          components={{
            noRowsFallback: (
              <div style={{ display: 'flex', justifyContent: 'center', gridColumnStart: '1', gridColumnEnd: '-1', marginTop: '32px' }}>
                {filterByErrors
                  ? t('spreadsheet_import.validation_step.no_rows_message_when_filtered')
                  : t('spreadsheet_import.validation_step.no_rows_message')}
              </div>
            ),
          }}
        />
      </div>

      {enablePaste &&
        <Menu id={COPY_ALL_MENU_ID} theme={theme.light}>
          <Item onClick={() => copyRows(data.filter(row => !isBlankRow(row)), true)}>
            {t('spreadsheet_import.validation_step.copy_all_with_header')}
          </Item>
          <Item onClick={() => copyRows(data.filter(row => !isBlankRow(row)), false)}>
            {t('spreadsheet_import.validation_step.copy_all')}
          </Item>
        </Menu>}

      {enableRowActions &&
        <Menu id={ROW_ACTIONS_MENU_ID} theme={theme.light}>
          <Item onClick={({ props }) => runRowAction('insertAbove', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.insert_above')}
          </Item>
          <Item onClick={({ props }) => runRowAction('insertBelow', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.insert_below')}
          </Item>
          <Item onClick={({ props }) => runRowAction('duplicate', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.duplicate')}
          </Item>
          <Item onClick={({ props }) => runRowAction('copy', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.copy')}
          </Item>
          <Item onClick={({ props }) => runRowAction('cut', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.cut')}
          </Item>
          <Item onClick={({ props }) => runRowAction('paste', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.paste')}
          </Item>
          <Item onClick={({ props }) => runRowAction('delete', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.delete')}
          </Item>
        </Menu>}

      {renderGridFooter && <div style={{ marginTop: '1rem' }}>{renderGridFooter(data)}</div>}

      {showAttachments && <div style={{ marginTop: '2rem' }} >
        <b> {t('spreadsheet_import.validation_step.charges_attachments')}</b>
        <Row style={{ display: 'flex', marginTop: '1rem' }} >
          <Col xl={12}>
            <FileSelector
              isDetails={false}
              isMulti={true}
              initialFiles={chargeAttachments}
              onFilesChanged={setValuesFiles}
              onRemoveFile={onRemoveFile}
              label={<Label>{t('common.add_new_file')} {hasAttachmentRequired ? '*' : ''}</Label>}
              smallButtonAdd={true}
            />
          </Col>
        </Row>
      </div>}

      </div>
      <div style={{ textAlign: 'right', paddingTop: 20, flexShrink: 0 }}>
        <Button preset={'secondary'} type="button" onClick={() => onClose()} text={t('common.cancel')} />
        {(!startWithBlankRows || onBack) &&
          <Button type="button" onClick={() => onPrevious()} text={t('spreadsheet_import.select_header_step.previous_button_title')} />}
        <Button text={submitButtonText ?? t('spreadsheet_import.validation_step.next_button_title')} size={'normal'} onClick={onContinue} />
      </div>

    </>
  );
};
